/**
 * Pure evaluation of an SMTP filename rule, shared by the Troubleshooting SMTP
 * tab. Mirrors the backend: frame_receiver_smtp_v2 runs
 * `re.search(expression, filename).groups()[0]` and matches that against a
 * camera name at the site; no match files the frame as UNIDENTIFIED_CAMERA.
 *
 * tabs/SmtpFilenameDistribution.tsx (the customer-facing helper, moved here
 * from the Site About page) has its own inline copy of this evaluation; this
 * module takes everything as arguments so findings and tests can use it.
 */

import { SmtpFilenamesResponse } from "../troubleshootingInterfaces";

export type RuleRowState = "ok" | "unknown" | "nomatch" | "invalid";

export interface RuleEvaluation {
  filename: string;
  extracted: string | null;
  span: [number, number] | null;
  state: RuleRowState;
  /** Camera whose name matches case-insensitively or ignoring separators, when there is no exact match. */
  nearest: string | null;
}

export interface CompiledRule {
  regex: RegExp | null;
  error: string | null;
}

export const compileRule = (expression: string): CompiledRule => {
  if (!expression) return { regex: null, error: null };
  try {
    return { regex: new RegExp(expression, "d"), error: null };
  } catch (error) {
    return { regex: null, error: (error as Error).message };
  }
};

const collapse = (value: string) => value.toLowerCase().replace(/[\s_-]/g, "");

export const nearestCameraName = (
  extracted: string,
  cameraNames: string[]
): string | null => {
  const lower = extracted.toLowerCase();
  const exactCi = cameraNames.find((name) => name.toLowerCase() === lower);
  if (exactCi) return exactCi;
  const collapsed = collapse(extracted);
  return cameraNames.find((name) => collapse(name) === collapsed) || null;
};

export const evaluateRule = (
  compiled: CompiledRule,
  filename: string,
  cameraNames: string[]
): RuleEvaluation => {
  if (compiled.error || !compiled.regex) {
    return {
      filename,
      extracted: null,
      span: null,
      state: "invalid",
      nearest: null,
    };
  }
  const match = compiled.regex.exec(filename);
  if (!match) {
    return {
      filename,
      extracted: null,
      span: null,
      state: "nomatch",
      nearest: null,
    };
  }
  // Python's groups()[0] is the first capture group; a rule with no group has
  // nothing to hand the receiver, so treat it as no match rather than using [0].
  if (match[1] === undefined) {
    return {
      filename,
      extracted: null,
      span: null,
      state: "nomatch",
      nearest: null,
    };
  }
  const extracted = match[1];
  const indices = (match as unknown as { indices?: Array<[number, number]> })
    .indices;
  const span = indices && indices[1] ? indices[1] : null;
  if (cameraNames.includes(extracted)) {
    return { filename, extracted, span, state: "ok", nearest: null };
  }
  return {
    filename,
    extracted,
    span,
    state: "unknown",
    nearest: nearestCameraName(extracted, cameraNames),
  };
};

/**
 * How the filenames a site recently sent fare under its saved rule, as the
 * SMTP tab's helper reports it (`onSummary`), but without the tab: Diagnosis
 * reads it straight from `customer/{id}/smtp_filenames/` so the finding does
 * not depend on having visited the SMTP Cameras tab (QA DG-18). With no
 * camera list to check against, the receiver's own match counts, as there.
 */
export const summarizeFilenames = (
  response: SmtpFilenamesResponse,
  cameraNames: string[]
): { rows: RuleEvaluation[]; counts: number[] } => {
  const compiled = compileRule(response.read_filename_expression);
  const rows = response.samples.map((sample) => {
    const row = evaluateRule(compiled, sample.filename, cameraNames);
    return row.state === "unknown" &&
      cameraNames.length === 0 &&
      sample.extracted_matches_existing_camera_id != null &&
      sample.extracted_camera_name === row.extracted
      ? { ...row, state: "ok" as const, nearest: null }
      : row;
  });
  return {
    rows,
    counts: response.samples.map((sample) => sample.count_12h ?? 1),
  };
};

export const RULE_PRESETS: { label: string; expression: string }[] = [
  { label: "Channel + underscore", expression: "^(ch\\d+)_" },
  { label: "Text before first _", expression: "^([^_]+)_" },
  { label: "Text before the date", expression: "^(.+?)_\\d{8}" },
  { label: "Whole name, without extension", expression: "^(.+)\\.[a-z0-9]+$" },
];
